import { useSelector } from 'react-redux'
import {
  Navigate,
  Outlet,
  useLocation,
  useParams,
  useSearchParams
} from 'react-router-dom'

import {
  AuthenticationType,
  PublicSessionToken
} from 'authentication/shared/types/authentication.type'
import { HTTPClient } from 'infrastructure/shared/api'
import apiAsync from 'infrastructure/shared/api/api-async'
import { SitemodelUuid } from 'sitemodel/shared/types'
import { useLocales } from 'locales/hooks'

const PrivateRoutes = () => {
  const [searchParams] = useSearchParams()
  const { sitemodelUuid } = useParams<SitemodelUuid>()
  const location = useLocation()

  const { getSitemodelByLocale } = useLocales()

  const { token: tokenInState, logoutStatus } = useSelector(
    (state: AuthenticationType) => state.authentication
  )

  const isLoggingOut = logoutStatus === 'logging_out'

  const tokenInURL = searchParams.get('token')

  const publicSessionToken: PublicSessionToken = window.publicSessionToken

  const token = tokenInURL ?? tokenInState ?? publicSessionToken

  const tokenType =
    !!token && token === publicSessionToken ? 'public' : 'private'

  const publicUrls: string[] = window.publicUrls

  const currentSitemodel = `/${sitemodelUuid ?? ''}`

  const isPublicUrl = publicUrls.includes(currentSitemodel)

  const isEditInURL = location.pathname.includes('/edit')

  const isPublicTokenEditMode = isEditInURL && tokenType === 'public'

  const isAuthenticating =
    !token || (!isPublicUrl && tokenType === 'public') || isPublicTokenEditMode

  const unauthorizedSitemodelUuid = getSitemodelByLocale({
    demand: 'unauthorizedSitemodelUuid'
  })

  const isUnauthorizedPage =
    !!sitemodelUuid && sitemodelUuid === unauthorizedSitemodelUuid

  if (isLoggingOut) {
    return <Navigate to={`/logged-out`} replace state={{ from: location }} />
  }

  if (isAuthenticating) {
    /**
     * we use React Router's location parts instead of the simpler
     * window.location.href because otherwise there is a syncing problem
     * sometimes and the navigation changes redirectOrigin to unauthorized
     * sitemodel url before React Router renders the new route, making the
     * parameter useless
     */
    const redirectOrigin = `${location.pathname}${location.search}${location.hash}`

    return (
      <Navigate
        to={`${unauthorizedSitemodelUuid}?redirectOrigin=${encodeURIComponent(redirectOrigin)}`}
        replace
        state={{ from: location }}
      />
    )
  }

  if (!!token && tokenType === 'private' && isUnauthorizedPage) {
    return <Navigate to={'/'} replace state={{ from: location }} />
  }

  apiAsync.setAuthToken(token)
  HTTPClient.setToken(token)

  return <Outlet />
}

export default PrivateRoutes
